









































































































































































































































































import Vue from 'vue';
import * as actions from '@/store/action-types';
import CustomerActivities from '@/components/CancelAnalytics/Customer/CustomerActivities.vue';
import { mapGetters, mapState } from 'vuex';
import { capitalizeFirstLetter, LOAD_STATE, healthScoreToLetterGrade, healthCategoryColor } from '@/store/shared';
import { ContentLoader } from 'vue-content-loader';
import { CopyToClipboard } from '@/mixins/copy-to-clipboard';
import userService from '@/api/user.service';
import GaugeWrapper from '@/components/Vis/D3/Charts/GaugeWrapper.vue';
import LineChartWrapper from '@/components/Vis/D3/Charts/LineChartWrapper.vue';
import { ICustomerHealth } from '@/store/types';
import ShortListLoader from '@/components/UI/Loader/ShortListLoader.vue';
import SmallListLoader from '@/components/UI/Loader/SmallListLoader.vue';
import LineLoader from '@/components/UI/Loader/LineLoader.vue';
import { isoCountries } from '@/helpers/iso3166-countries';

const groups = [
  { label: 'Low Risk', key: 'low', color: '#52A475', icon: '🏝️', bgColor: '#D9F9E6', textColor: '#163523' },
  { label: 'Medium Risk', key: 'medium', color: '#B06FD3', icon: '🚸', bgColor: '#F2E7F8', textColor: '#431646' },
  { label: 'High Risk', key: 'high', color: '#EE7963', icon: '🏜️', bgColor: '#FAE7E2', textColor: '#461A11' },
];

export default Vue.extend({
  name: 'CustomerProfile',
  components: {
    CustomerActivities,
    ContentLoader,
    GaugeWrapper,
    LineChartWrapper,
    SmallListLoader,
    ShortListLoader,
    LineLoader,
  },
  mixins: [CopyToClipboard],
  data() {
    return {
      showDeleteCustomerModal: false,
      deleteError: false,
      deleting: false,
    };
  },
  async created() {
    const { id } = this.$route.params;
    this.$store.dispatch(actions.FETCH_CUSTOMER_PROFILE, id);
  },
  computed: {
    ...mapState(['customerProfile', 'customerProfileLoadState']),
    ...mapGetters([
      'customerInvoicesTotal',
      'customerSubscriptionPriceToString',
      'provider',
      'isLocal',
      'isAdmin',
      'stripeAccountId',
      'customerSubscriptions',
      'confidential',
    ]),
    riskGroup(): any {
      return groups.find((g) => g.key === this.customerHealth?.combinedHealth?.risk);
    },
    loading(): boolean {
      return this.customerProfileLoadState === LOAD_STATE.LOADING;
    },
    customer(): any | null {
      return this.customerProfile?.customer;
    },
    isTestMode(): boolean {
      const mode = new URLSearchParams(window.location.search).get('mode');
      return mode?.toLowerCase() === 'test';
    },
    customerEmail(): string | null {
      if (this.customer?.email) {
        return this.customer.email;
      }
      // else look through activities for email
      for (let i = 0; i < this.activities.length; i++) {
        const activity = this.activities[i];
        if (activity.customerEmail) {
          return activity.customerEmail;
        }
        if (activity.customer?.email) {
          return activity.customer.email;
        }
      }
      return null;
    },
    activities(): any[] {
      return this.customerProfile?.activities;
    },
    customerName(): string | null {
      return this.customer?.name;
    },
    customerHealth(): ICustomerHealth | null {
      return this.$store.state.customerHealth;
    },
    source(): any {
      // @ts-ignore
      return this.customerProfile?.customer?.default_source;
    },
    sourceCountry(): string {
      if (this.source?.country) {
        const match = isoCountries.find((c) => c.alpha2 === this.source.country);
        if (match) {
          if (match.country.length > 20) {
            return match.alpha3;
          }
          return match.country;
        }
      }
      return '-';
    },
    sourceCountryFlag(): string {
      if (this.source?.country) {
        const match = isoCountries.find((c) => c.alpha2 === this.source.country);
        if (match) {
          return `https://flagcdn.com/w80/${match.alpha2.toLowerCase()}.png`;
        }
      }
      return '';
    },
    healthScore(): number {
      if (this.customerHealth?.combinedHealth?.healthScore) {
        return this.customerHealth.combinedHealth.healthScore;
      }
      return 5;
    },
    healthLabel(): string {
      // return this.customerHealth?.combinedHealth?.risk || 'Neutral';
      return healthScoreToLetterGrade(this.healthScore);
    },
    healthColor(): string {
      const cat = this.customerHealth?.combinedHealth.risk;
      return cat ? healthCategoryColor[cat] : '#444';
    },
    // for line chart
    subscriptionHealthSeries(): any {
      const subs = this.customerHealth?.subscriptions;
      // @ts-ignore
      const subCount = subs.length;
      // @ts-ignore
      return subs
        .filter((s: any) => s.healthScores.length)
        .map((s: any) => {
          const subId = s.id;
          // const healthHistory = this.customerHealth?.subscriptions?.find((h: any) => h.subId === subId)?.healthScores || ([] as number[]);
          const healthHistory = s.healthScores || ([] as number[]);
          return {
            // label: subCount === 1 ? '' : `${s.nickname} (${s.priceLabel})`,
            label: '',
            id: s.id,
            data: healthHistory
              .map((h: any) => {
                return {
                  timestamp: new Date(h.timestamp),
                  score: h.emaSlow,
                };
              })
              .sort((a: any, b: any) => a.timestamp - b.timestamp),
          };
        });
    },
    healthDelta(): number {
      const series = this.subscriptionHealthSeries[0]?.data || [];

      const lookback = Math.min(3, series.length) - 1;
      if (series.length > 1) {
        return (series[series.length - 1].score - series[series.length - 1 - lookback].score) / series[series.length - 1 - lookback].score;
      }
      return 0;
    },
    dateFormatSubscription(): string | Date {
      if (this.customer.decorated?.customerSubscription?.subscriptionStartDate) {
        return new Date(this.customer.decorated?.customerSubscription?.subscriptionStartDate);
      }
      return '';
    },
    customerSubscriptionStatus(): string {
      if (this.customer?.decorated?.customerSubscription?.status) {
        return capitalizeFirstLetter(this.customer.decorated.customerSubscription.status);
      }
      return '-';
    },
    providerCustomerLink(): string {
      if (this.provider === 'stripe') {
        if (this.isAdmin) {
          if (this.isTestMode) {
            return `https://dashboard.stripe.com/${this.stripeAccountId}/test/customers/${this.customer?.id}`;
          }
          return `https://dashboard.stripe.com/${this.stripeAccountId}/customers/${this.customer?.id}`;
        }
        // link to stripe customer
        if (this.isTestMode) {
          return `https://dashboard.stripe.com/test/customers/${this.customer?.id}`;
        }
        return `https://dashboard.stripe.com/customers/${this.customer?.id}`;
      }
      if (this.provider === 'chargebee') {
        // link to chargebee customer
        return `https://app.chargebee.com/${this.customer?.id}`;
      }
      if (this.provider === 'paddle') {
        // link to paddle customer
        return `https://vendors.paddle.com/customer/${this.customer?.id}`;
      }
      return '';
    },
  },
  methods: {
    async deleteCustomer() {
      this.deleting = true;
      try {
        if (this.customerEmail) {
          const result = await userService.deleteEndUser(this.$store.state.user._id, { email: this.customerEmail });
        } else {
          throw new Error('No customer email found');
        }
        // element ui success notification
        this.$notify({
          title: 'Success',
          message: 'Customer deleted successfully',
          type: 'success',
          duration: 5000,
        });
        // redirect home
        this.$router.push({ name: 'cancellation.metrics' });
      } catch (e) {
        this.deleteError = true;
      } finally {
        this.deleting = false;
      }
    },
  },
});
